import { APP_PLANS, getAppPlanRank, type AppPlanId } from './appPlans';
import { IconCheck } from './appIcons';

interface AppPricingViewProps {
  plans?: typeof APP_PLANS;
  currentPlanId: AppPlanId;
  isSignedIn: boolean;
  onSelectPlan: (id: AppPlanId) => void;
  onCancelSubscription: () => void;
  onLogin: () => void;
  isBusy?: boolean;
  error?: string;
  hasSubscription?: boolean;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
}

const formatMonthlyPrice = (priceUsd: number): string => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(priceUsd);
};

function AppPricingView({
  plans: availablePlans,
  currentPlanId,
  isSignedIn,
  onSelectPlan,
  onCancelSubscription,
  onLogin,
  isBusy = false,
  error,
  hasSubscription = false,
  cancelAtPeriodEnd,
  currentPeriodEnd
}: AppPricingViewProps) {
  const plans = availablePlans ?? APP_PLANS;
  const currentRank = getAppPlanRank(currentPlanId);

  return (
    <div className="app-scroll">
      <div className="app-pricing-page">
        <header className="app-pricing-intro">
          <h1>Plans</h1>
          <p>
            Havu is your AI AppSec lead. It assesses your code, answers the security questions insurers, investors
            and customers ask, and prepares quote requests when you need a human audit.
          </p>
        </header>

        <div className="app-pricing-tiers">
          {plans.map((plan, index) => {
            const rank = getAppPlanRank(plan.id);
            const isCurrent = isSignedIn && plan.id === currentPlanId;
            const previous = plans[index - 1];

            return (
              <section
                key={plan.id}
                className={`app-tier${isCurrent ? ' current' : ''}`}
                aria-labelledby={`app-tier-${plan.id}`}
              >
                <div className="app-tier-head">
                  <h2 id={`app-tier-${plan.id}`}>{plan.name}</h2>
                  {isCurrent && <span className="app-tier-badge">Current plan</span>}
                </div>
                <p className="app-tier-tagline">{plan.tagline}</p>
                <p className="app-tier-price">
                  <strong>{formatMonthlyPrice(plan.priceUsd)}</strong>
                  <span>per month</span>
                </p>

                <button
                  type="button"
                  className={`app-tier-action${rank > currentRank ? ' primary' : ''}`}
                  disabled={isCurrent || isBusy || plan.available === false}
                  onClick={() => isSignedIn ? onSelectPlan(plan.id) : onLogin()}
                >
                  {plan.available === false
                    ? 'Unavailable'
                    : isCurrent
                    ? 'Current plan'
                    : !isSignedIn
                      ? 'Log in'
                      : rank > currentRank ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`}
                </button>

                <div className="app-tier-features">
                  {previous && <p>Everything in {previous.name}, plus:</p>}
                  <ul>
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <IconCheck aria-hidden={true} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            );
          })}
        </div>

        <p className="app-pricing-note">
          Prices in USD, billed monthly. Assessment allowances count standard assessments; a custom workflow can
          use more than one. Paid allowances refresh when the subscription renews. Havu checks your applications whenever
          you open it and works from the assessments you run; it does not watch them between assessments.
        </p>
        {cancelAtPeriodEnd && (
          <p className="app-pricing-note">
            Your subscription is canceled and remains active
            {currentPeriodEnd ? ` until ${new Date(currentPeriodEnd).toLocaleDateString()}` : ' through the current billing period'}.
          </p>
        )}
        {isSignedIn && hasSubscription && (
          <button
            type="button"
            className="app-pricing-cancel"
            disabled={isBusy || cancelAtPeriodEnd}
            onClick={onCancelSubscription}
          >
            {cancelAtPeriodEnd ? 'Cancellation scheduled' : 'Cancel subscription'}
          </button>
        )}
        {error && <p className="app-widget-error" role="alert">{error}</p>}
      </div>
    </div>
  );
}

export default AppPricingView;
